import moment from 'moment';
import groupBy from 'lodash/groupBy';
import { isArray } from 'utils/arrays';
import { isString } from 'utils/strings';

// https://tc39.es/proposal-temporal/docs/index.html
const PATTERNS = {
  PLAIN_DATE: /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})$/,
  PLAIN_TIME: /^(?<hour>\d{2}):(?<minutes>\d{2})(?<secondschunk>:(?<seconds>\d{2}))?$/,
  PLAIN_DATETIME: /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})T(?<hour>\d{2}):(?<minutes>\d{2})(?<secondschunk>:(?<seconds>\d{2}))?$/,
};

const MACHINE_FORMATS = {
  PLAIN_DATE: 'YYYY-MM-DD',
  PLAIN_TIME: 'HH:mm',
  PLAIN_DATETIME: 'YYYY-MM-DD[T]HH:mm',
  PLAIN_YEARMONTH: 'YYYY-MM',
};

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
// https://moment.github.io/luxon/#/formatting?id=presets
const FORMAT_OPTIONS = {
  DATE_SHORT: {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  },
  TIME_SIMPLE: {
    hour: '2-digit',
    minute: '2-digit',
  },
  DATETIME_SHORT: {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
  DATETIME_HUGE_UTC: {
    dateStyle: 'full',
    timeStyle: 'long',
    timeZone: 'UTC',
  },
};

/**
 * Comprueba si un valor es de tipo Date
 * @see https://stackoverflow.com/questions/1353684/detecting-an-invalid-date-date-instance-in-javascript
 * @param {*} date
 * @returns {boolean}
 */
const isDate = date => date instanceof Date && !isNaN(date);

/**
 * Devuelve los valores de zonas horarias soportadas
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/supportedValuesOf
 * @returns {Array.<string>}
 */
const getTimeZones = () => Intl.supportedValuesOf('timeZone');

/**
 * Devuelve la zona horaria actual en formato IANA
 * @see https://developer.mozilla.org/es/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat
 * @see https://stackoverflow.com/questions/1091372/getting-the-clients-timezone-offset-in-javascript
 * @returns {string}
 */
const getTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Formatea una zona horaria en base al idioma
 * @param   {Object} arg
 * @param   {string} [arg.locale]
 * @param   {string} arg.timeZone
 * @param   {string} [arg.timeZoneName]
 * @returns {string}
 */
const formatTimeZone = ({ locale = 'default', timeZone, timeZoneName = 'shortOffset' }) => {
  const options = { locale, timeZone, timeZoneName, day: '2-digit' };
  const dateTimeFormatted = new Intl.DateTimeFormat(locale, options).format();
  const name = dateTimeFormatted.slice(4);
  return name;
};

/**
 * Devuelve la zona horaria con el offset como prefijo
 * @param   {Object} arg
 * @param   {string} [arg.locale]
 * @param   {string} arg.timeZone
 * @returns {string}
 */
const getTimeZoneWithShortOffset = ({ locale = 'default', timeZone = getTimeZone() } = {}) => {
  const timeZoneOffset = formatTimeZone({ locale, timeZone, timeZoneName: 'shortOffset' });
  return `${timeZoneOffset} ${timeZone}`;
};

/**
 * Comprueba si una fecha tiene el formato "YYYY-MM-DD".
 * Representa una fecha de calendario que no está asociada con ninguna hora ni zona horaria.
 * Ejemplo: 24 de agosto de 2006
 * @see https://tc39.es/proposal-temporal/docs/index.html#Temporal-PlainDate
 * @param {string} date
 * @returns {boolean}
 */
const isPlainDate = date => {
  return isString(date) && PATTERNS.PLAIN_DATE.test(date);
};

/**
 * Parsea una fecha en formato PlainDate a Date.
 *
 * @IMPORTANT
 * Se usa el constructor con year, month, day... No se usa "YYYY-MM-DD" porque Date lo trataría como una fecha UTC
 * y puede haber diferencias en el día dependiendo de la zona horaria...
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date#parameters
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/parse#date_time_string_format
 *
 * @param {string} date
 * @returns {Date}
 */
const parseFromPlainDateToJSDate = date => {
  const { year, month, day } = date.match(PATTERNS.PLAIN_DATE).groups;
  const monthIndex = +month - 1;
  return new Date(+year, monthIndex, +day);
};

/**
 * Comprueba si una hora tiene el formato "HH:mm".
 * Representa una hora que no está asociada con ninguna fecha ni zona horaria.
 * Ejemplo: 24 de agosto de 2006
 * @see https://tc39.es/proposal-temporal/docs/index.html#Temporal-PlainTime
 * @param {string} date
 * @returns {boolean}
 */
const isPlainTime = time => {
  return isString(time) && PATTERNS.PLAIN_TIME.test(time);
};

/**
 * Parsea una fecha en formato PlainDate a Date.
 *
 * @param {string} time
 * @returns {Date}
 */
const parseFromPlainTimeToJSDate = time => {
  const { hour, minutes, seconds } = time.match(PATTERNS.PLAIN_TIME).groups;
  const safeSeconds = seconds || 0;
  const date = new Date();
  date.setHours(+hour, +minutes, +safeSeconds);
  return date;
};

/**
 * Comprueba si una fecha hora tiene el formato "YYYY-MM-DD[T]HH:mm:ss".
 * Representa una fecha de calendario y hora que no contiene información de zona horaria.
 * Ejemplo: 7 de diciembre de 1995 a las 15:00
 * @see https://tc39.es/proposal-temporal/docs/index.html#Temporal-PlainDateTime
 * @param {string} date
 * @returns {boolean}
 */
const isPlainDateTime = date => {
  return isString(date) && PATTERNS.PLAIN_DATETIME.test(date);
};

/**
 * Parsea una fecha hora en formato PlainDateTime a Date.
 *
 * @IMPORTANT
 * Se usa el constructor con year, month, day... No se usa "YYYY-MM-DD[T]HH:mm:ss" porque Date lo trataría como una fecha LOCAL
 * y puede haber diferencias en el día hora dependiendo de la zona horaria
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date#parameters
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/parse#date_time_string_format
 *
 * @param {string} date
 * @returns {Date}
 */
const parseFromPlainDateTimeToJSDate = date => {
  const { year, month, day, hour, minutes, seconds } = date.match(PATTERNS.PLAIN_DATETIME).groups;
  const monthIndex = +month - 1;
  const safeSeconds = seconds || 0;
  return new Date(+year, monthIndex, +day, +hour, +minutes, +safeSeconds);
};

/**
 * @TODO Mejorar la validación
 *
 * Comprueba si una fecha hora tiene el formato ISO (PlainDateTime con Offset o el sufijo "Z").
 * Representa un punto fijo en el tiempo (llamado "tiempo exacto") sin tener en cuenta el calendario o la ubicación.
 * Ejemplo: 20 de julio de 1969 a las 20:17 UTC
 * @see https://tc39.es/proposal-temporal/docs/index.html#Temporal-Instant
 * @param {string} date
 * @returns {boolean}
 */
const isInstant = date => isString(date);

/**
 * Parsea una fecha en formato ISO (PlainDateTime con Offset o el sufijo "Z") a Date
 * @param {string} date
 * @returns {Date}
 */
const parseFromInstantToJSDate = date => {
  return moment(date).toDate();
};

/**
 * Parsea una fecha a Date.
 * La fecha puede ser: PlainDate, PlainDateTime, Instant, Number...
 * @requires isPlainDate
 * @requires parseFromPlainDateToDateObject
 * @requires isPlainDateTime
 * @requires parseFromPlainDateTimeToDateObject
 * @requires isInstant
 * @requires parseFromInstantToDateObject
 *
 * @param {Object} arg
 * @param {string|number|Object} arg.date
 * @returns {Date}
 */
const parseFromAnyDateToJSDate = ({ date }) => {
  if (isPlainDate(date)) return parseFromPlainDateToJSDate(date);
  if (isPlainTime(date)) return parseFromPlainTimeToJSDate(date);
  if (isPlainDateTime(date)) return parseFromPlainDateTimeToJSDate(date);
  if (isInstant(date)) return parseFromInstantToJSDate(date);
  return moment(date).toDate();
};

/**
 * Parsea una fecha a PlainDate.
 * El resultado es útil para usalo como valor de un input date.
 * @param {Object} arg
 * @param {string} arg.date
 * @returns {string}
 */
const parseFromDateToPlainDate = ({ date }) => {
  return moment(date).format(MACHINE_FORMATS.PLAIN_DATE);
};

/**
 * Parsea una fecha a PlainTime.
 * El resultado es útil para usalo como valor de un input time.
 * @param {Object} arg
 * @param {string} arg.date
 * @returns {string}
 */
const parseFromDateToPlainTime = ({ date }) => {
  return moment(date).format(MACHINE_FORMATS.PLAIN_TIME);
};

/**
 * Parsea una fecha a PlainDateTime.
 * El resultado es útil para usalo como valor de un input datetime-local.
 * @param {Object} arg
 * @param {string} arg.date
 * @returns {string}
 */
const parseFromDateToPlainDateTime = ({ date }) => {
  return moment(date).format(MACHINE_FORMATS.PLAIN_DATETIME);
};

/**
 * Parsea una fecha a PlainYearMonth.
 * @param {Object} arg
 * @param {string} arg.date
 * @returns {string}
 */
const parseFromDateToPlainYearMonth = ({ date }) => {
  return moment(date).format(MACHINE_FORMATS.PLAIN_YEARMONTH);
};

/**
 * Parsea una fecha a Instant con formato UTC.
 * @param {Object} arg
 * @param {string} arg.date
 * @returns {string}
 */
const parseFromDateToInstantUTC = ({ date }) => {
  return moment.utc(date).format();
};

/**
 * Formatea una fecha en base al idioma
 * @param   {Object} arg
 * @param   {string} [arg.locale]
 * @param   {Date}   arg.date
 * @param   {Object} [arg.options]
 * @returns {string}
 */
const formatDate = ({ locale = 'default', date, options = FORMAT_OPTIONS.DATE_SHORT }) => {
  return new Intl.DateTimeFormat(locale, options).format(date);
};

/**
 * Formatea una fecha hora en base al idioma
 * @param   {Object} arg
 * @param   {string} [arg.locale]
 * @param   {Date}   arg.date
 * @param   {Object} [arg.options]
 * @returns {string}
 */
const formatDateTime = ({ locale = 'default', date, options = FORMAT_OPTIONS.DATETIME_SHORT }) => {
  return new Intl.DateTimeFormat(locale, options).format(date);
};

/**
 * Formatea un PlainDate en base al idioma
 * @param   {Object} arg
 * @param   {string} [arg.locale]
 * @param   {string} arg.date
 * @param   {Object} [arg.options]
 * @returns {string}
 */
const formatPlainDate = ({ locale, date, options }) => {
  const jsDate = parseFromPlainDateToJSDate(date);
  return formatDate({ locale, date: jsDate, options });
};

/**
 * Formatea un PlainDateTime en base al idioma
 * @param   {Object} arg
 * @param   {string} [arg.locale]
 * @param   {string} arg.date
 * @param   {Object} [arg.options]
 * @returns {string}
 */
const formatPlainDateTime = ({ locale, date, options }) => {
  const jsDate = parseFromPlainDateTimeToJSDate(date);
  return formatDateTime({ locale, date: jsDate, options });
};

/**
 * Formatea un Instant en base al idioma
 * @param   {Object} arg
 * @param   {string} [arg.locale]
 * @param   {string} arg.date
 * @param   {Object} [arg.options]
 * @returns {string}
 */
const formatInstant = ({ locale, date, options }) => {
  const jsDate = parseFromInstantToJSDate(date);
  return formatDateTime({ locale, date: jsDate, options });
};

/**
 * Formatea una fecha en base al idioma
 * La fecha puede ser: PlainDate, PlainDateTime, Instant, Number...
 * @requires isPlainDate
 * @requires formatDate
 * @requires formatDateTime
 *
 * @param {Object} arg
 * @param {string} [arg.locale]
 * @param {string|number|Object} arg.date
 * @param {Object} [arg.options]
 * @returns {string}
 */
const formatAnyDate = ({ locale, date, options }) => {
  const jsDate = parseFromAnyDateToJSDate({ date });

  return isPlainDate(date)
    ? formatDate({ locale, date: jsDate, options })
    : formatDateTime({ locale, date: jsDate, options });
};

/**
 * Parsea un rango de fechas a formato de array
 * @param {Object|Array} range
 * @returns {Array}
 */
const parseFromDateRangeToDateRangeArray = range => {
  return isArray(range) ? range : [range.beginDate, range.endDate];
};

/**
 * Formatea un rango de fechas en base al idioma
 * @param   {Object} arg
 * @param   {string} [arg.locale]
 * @param   {Array.<Date>} arg.date
 * @param   {Object} [arg.options]
 * @returns {string}
 */
const formatDateRange = ({ locale = 'default', range, options = FORMAT_OPTIONS.DATE_SHORT }) => {
  const [startDate, endDate] = range;
  return new Intl.DateTimeFormat(locale, options).formatRange(startDate, endDate);
};

/**
 * Formatea un rango de fechas con hora en base al idioma
 * @param   {Object} arg
 * @param   {string} [arg.locale]
 * @param   {Array.<Date>} arg.date
 * @param   {Object} [arg.options]
 * @returns {string}
 */
const formatDateTimeRange = ({
  locale = 'default',
  range,
  options = FORMAT_OPTIONS.DATETIME_SHORT,
}) => {
  const [startDate, endDate] = range;
  return new Intl.DateTimeFormat(locale, options).formatRange(startDate, endDate);
};

/**
 * Formatea un rango de fechas en base al idioma
 * Las fechas del rango pueden ser: PlainDate, PlainDateTime, Instant, Number...
 * @requires isPlainDate
 * @requires formatDateRange
 * @requires formatDateTimeRange
 *
 * @param {Object} arg
 * @param {string} [arg.locale]
 * @param {Array.<string|number|Object>} arg.range
 * @param {Object} [arg.options]
 * @returns {string}
 */
const formatAnyDateRange = ({ locale, range, options }) => {
  const [startDate, endDate] = parseFromDateRangeToDateRangeArray(range);

  const jsStartDate = parseFromAnyDateToJSDate({ date: startDate });
  const jsEndDate = parseFromAnyDateToJSDate({ date: endDate });
  const jsRange = [jsStartDate, jsEndDate];

  return isPlainDate(startDate)
    ? formatDateRange({ locale, range: jsRange, options })
    : formatDateTimeRange({ locale, range: jsRange, options });
};

/**
 * Devuele el momento transcurrido desde la fecha indicada
 * @param {*} arg
 * @param {string|Date|Moment} arg.date
 * @returns {string}
 */
const formatDateFromNow = ({ date }) => moment(date).fromNow();

/**
 * Devuelve la fecha de hoy como PlainDate
 * @returns {string}
 */
const getTodayAsPlainDate = () => moment().format(MACHINE_FORMATS.PLAIN_DATE);

/**
 * Devuelve la fecha hora actual como PlainDateTime
 * @returns {string}
 */
const getNowAsPlainDateTime = () => moment().format(MACHINE_FORMATS.PLAIN_DATETIME);

/**
 * Devuelve un array de días de la semana en base al idioma
 * @param {Object} arg
 * @param {string} arg.format
 * @returns {Array.<string>}
 */
const getArrayOfWeekdays = ({ format = 'short' } = {}) => {
  return Array.from({ length: 7 }).map((item, index) => {
    const date = new Date(Date.UTC(1970, 5, index + 1));
    return formatDate({ date, options: { weekday: format } });
  });
};

/**
 * Devuelve un array de fechas desde beginDate hasta endDate
 * @param   {Object}         arg
 * @param   {string|Moment}  arg.beginDate - Fecha inicio
 * @param   {string|Moment}  arg.endDate   - Fecha fin
 * @param   {string}         [arg.format]  - Formato para las fechas
 * @returns {Array.<string>}
 */
const getArrayOfDates = ({ beginDate, endDate, format = MACHINE_FORMATS.PLAIN_DATE }) => {
  const datesDiff = moment(endDate).diff(moment(beginDate), 'days') + 1;
  const dates = Array.from({ length: datesDiff }, (x, index) =>
    moment(beginDate)
      .add(index, 'day')
      .format(format),
  );
  return dates;
};

/**
 * Devuelve un array de días de meses
 * @param {Object} arg
 * @param {string} arg.format
 * @returns {Array.<string>}
 */
const getArrayOfMonths = ({ format = 'short' } = {}) => {
  return Array.from({ length: 12 }).map((item, index) => {
    const date = new Date(0, index);
    return formatDate({ date, options: { month: format } });
  });
};

/**
 * Comprueba si una fecha esta contenida en un rango
 * @param   {Object}                arg
 * @param   {string|Moment}         arg.date               - Fecha
 * @param   {Array.<string|Moment>} arg.range              - Rango [beginDate, endDate]
 * @param   {string}                [arg.granularity]      - Granularidad para comparar, por defecto milisegundos
 * @param   {string}                [arg.inclusivity='[]'] - Indicadores para incluir o excluir los extremos de los rangos. Ej.: (), [), (], []
 * @returns {boolean}
 */
const isDateBetweenDateRange = ({ date, range, granularity, inclusivity = '[]' }) => {
  const [beginDate, endDate] = range;
  return moment(date).isBetween(beginDate, endDate, granularity, inclusivity);
};

/**
 * Comprueba si un rango está contenido en otro rango
 * @param   {Object}                arg
 * @param   {Array.<string|Moment>} arg.source        - Rango fuente [beginDate, endDate]
 * @param   {Array.<string|Moment>} arg.target        - Rango destino [beginDate, endDate]
 * @param   {string}                [arg.granularity] - Granularidad para comparar, por defecto milisegundos
 * @returns {boolean}
 */
const isDateRangeInDateRange = ({ source, target, granularity }) => {
  const [sourceBegin, sourceEnd] = source;
  const [targetBegin, targetEnd] = target;
  return (
    moment(targetBegin).isSameOrBefore(sourceBegin, granularity) &&
    moment(targetEnd).isSameOrAfter(sourceEnd, granularity)
  );
};

/**
 * Comprueba si dos rangos de fechas se superponen en algún punto
 * @requires isDateBetweenDateRange
 * @requires isDateRangeInDateRange
 * @param   {Object}                arg
 * @param   {Array.<string|Moment>} arg.rangeA             - Rango [beginDate, endDate]
 * @param   {Array.<string|Moment>} arg.rangeB             - Rango [beginDate, endDate]
 * @param   {string}                [arg.granularity]      - Granularidad para comparar, por defecto milisegundos
 * @param   {string}                [arg.inclusivity='[]'] - Indicadores para incluir o excluir los extremos de los rangos. Ej.: (), [), (], []
 * @returns {boolean}
 */
const areDateRangesOverlapping = ({ rangeA, rangeB, granularity, inclusivity = '[]' }) => {
  const [beginA, endA] = rangeA;

  if (isDateBetweenDateRange({ date: beginA, range: rangeB, granularity, inclusivity })) {
    return true;
  }
  if (isDateBetweenDateRange({ date: endA, range: rangeB, granularity, inclusivity })) {
    return true;
  }
  if (isDateRangeInDateRange({ source: rangeA, target: rangeB, granularity })) {
    return true;
  }
  if (isDateRangeInDateRange({ source: rangeB, target: rangeA, granularity })) {
    return true;
  }
  return false;
};

/**
 * Devuelve el año al que pertenece un día
 * @param   {string|Moment} date
 * @returns {number}
 */
const getIsoYearFromDate = date => moment(date).isoWeekYear();

/**
 * Devuelve un id con el Año ISO y la Semana ISO separados por un guión
 * @param {Object} arg
 * @param {string|Moment} arg.date
 * @param {string} arg.separator
 * @returns {string}
 */
const getIsoWeekIdFromDate = ({ date, separator = 'W' }) => {
  return `${moment(date).format('GGGG')}${separator}${moment(date).format('WW')}`;
};

/**
 * Devuelve un rango de fechas en base a la semana ISO
 * @param {Object} arg
 * @param {string} arg.isoWeek
 * @param {string} arg.format
 * @returns {Object}
 */
const getDateRangeFromIsoWeek = ({ isoWeek, format = MACHINE_FORMATS.PLAIN_DATE }) => {
  const startDate = moment(isoWeek)
    .startOf('isoWeek')
    .format(format);
  const endDate = moment(isoWeek)
    .endOf('isoWeek')
    .format(format);

  return { startDate, endDate };
};

/**
 * Devuelve el inicio de la primera semana ISO dada una fecha de inicio (ya sea mes, año...)
 * Para identificar la primera semana existen varias definiciones, una de ellas es: "la que contiene el primer jueves"
 * @see https://en.wikipedia.org/wiki/ISO_week_date
 * @see https://en.wikipedia.org/wiki/ISO_week_date#First_week
 * @see https://github.com/moment/moment/issues/3696
 * @param   {string|Moment} startDate
 * @returns {Moment}
 */
const getStartOfIsoWeekBelongsToStart = startDate => {
  const isWeekBelongToStart = moment(startDate).isoWeekday() <= 4;
  return moment(startDate)
    .startOf('isoWeek')
    .add(isWeekBelongToStart ? 0 : 1, 'weeks');
};

/**
 * Devuelve el fin de la última semana ISO dada una fecha de fin (ya sea mes, año...)
 * Para identificar la primera semana existen varias definiciones, una de ellas es: "la que contiene el primer jueves"
 * @see https://en.wikipedia.org/wiki/ISO_week_date
 * @see https://en.wikipedia.org/wiki/ISO_week_date#First_week
 * @see https://github.com/moment/moment/issues/3696
 * @param   {string|Moment} endDate
 * @returns {Moment}
 */
const getEndOfIsoWeekBelongsToEnd = endDate => {
  const isWeekBelongToEnd = moment(endDate).isoWeekday() >= 4;
  return moment(endDate)
    .endOf('isoWeek')
    .subtract(isWeekBelongToEnd ? 0 : 1, 'weeks');
};

/**
 * Devuelve el inicio de la semana ISO
 * @param   {string|Moment} date
 * @returns {Moment}
 */
const getStartOfIsoWeek = date => moment(date).startOf('isoWeek');

/**
 * Devuelve el fin de la semana ISO
 * @param   {string|Moment} date
 * @returns {Moment}
 */
const getEndOfIsoWeek = date => moment(date).endOf('isoWeek');

/**
 * Nota: Aparentemente no existe una definición ISO para un mes
 * Devuelve el inicio del mes a partir de una fecha (será la primera semana que contenga un jueves)
 * Esta implementación se situa en el jueves de la semana actual para obtener el mes al que pertenece,
 * luego obtiene la fecha de inicio de dicho mes y por último el inicio de la primera semana.
 * @see https://en.wikipedia.org/wiki/ISO_week_date#Weeks_per_month
 * @requires getStartOfIsoWeekBelongsToStart
 * @param    {string|Moment} date
 * @returns  {Moment}
 */
const getStartOfIsoMonth = date => {
  const thursdayOfWeek = moment(date).isoWeekday(4);
  const startOfMonth = moment(thursdayOfWeek).startOf('month');
  const startOfIsoMonth = getStartOfIsoWeekBelongsToStart(startOfMonth);
  return startOfIsoMonth;
};

/**
 * Nota: Aparentemente no existe una definición ISO para un mes
 * Devuelve el fin del mes a partir de una fecha (será la última semana que contenga un jueves)
 * Esta implementación se situa en el jueves de la semana actual para obtener el mes al que pertenece,
 * luego obtiene la fecha de fin de dicho mes y por último el fin de la última semana.
 * @see https://en.wikipedia.org/wiki/ISO_week_date#Weeks_per_month
 * @requires getEndOfIsoWeekBelongsToEnd
 * @param    {string|Moment} date
 * @returns  {Moment}
 */
const getEndOfIsoMonth = date => {
  const thursdayOfWeek = moment(date).isoWeekday(4);
  const endOfMonth = moment(thursdayOfWeek).endOf('month');
  const endOfIsoMonth = getEndOfIsoWeekBelongsToEnd(endOfMonth);
  return endOfIsoMonth;
};

/**
 * Devuelve el inicio del año a partir de una fecha (será la primera semana que contenga un jueves)
 * Ejemplos: 2019: 31-12-2018, 2020: 30-12-2019, 2021: 04-01-2021
 * @requires getStartOfIsoWeekBelongsToStart
 * @param    {string|Moment} date
 * @returns  {Moment}
 */
const getStartOfIsoYear = date => {
  const startOfYear = moment(date).startOf('year');
  const startOfIsoYear = getStartOfIsoWeekBelongsToStart(startOfYear);
  return startOfIsoYear;
};

/**
 * Devuelve el fin del año a partir de una fecha (será la última semana que contenga un jueves)
 * Ejemplos: 2019: 29-12-2019, 2020: 03-12-2021, 2021:02-12-2022
 * @requires getEndOfIsoWeekBelongsToEnd
 * @param    {string|Moment} date
 * @returns  {Moment}
 */
const getEndOfIsoYear = date => {
  const endOfYear = moment(date).endOf('year');
  const endOfIsoYear = getEndOfIsoWeekBelongsToEnd(endOfYear);
  return endOfIsoYear;
};

/**
 * Devuelve la diferencia entre el inicio y fin de un rango
 * @param   {*}      arg
 * @param   {Object} arg.range      - Rango de fechas con beginDate y endDate
 * @param   {string} arg.unitOfTime - Unidad de tiempo para calcular la diferencia
 * @returns {number}
 */
const getDateRangeDiff = ({ range, unitOfTime = 'days' }) => {
  const { beginDate, endDate } = range;
  return endDate ? moment(endDate).diff(moment(beginDate), unitOfTime) + 1 : 0;
};

/**
 * Devuelve un objeto con el año y número de la semana como clave y un array de fechas como valor
 * @requires getIsoWeekIdFromDate
 * @param   {Array.<string|Moment>} dates
 * @returns {Object}
 */
const getDatesGroupedByIsoWeek = dates => {
  return groupBy(dates, date => getIsoWeekIdFromDate({ date, separator: '-' }));
};

/**
 * Devuelve una colección de rangos de semanas completas dado un rango de fechas
 * @requires getArrayOfDates
 * @requires getDatesGroupedByIsoWeek
 * @param   {Object}         arg
 * @param   {string|Moment}  arg.beginDate - Fecha inicio
 * @param   {string|Moment}  arg.endDate   - Fecha fin
 * @param   {string}         [arg.format]  - Formato para las fechas
 * @returns {Object}
 */
const getFullWeekRangesFromDateRange = ({ beginDate, endDate, format }) => {
  const dates = getArrayOfDates({ beginDate, endDate, format });
  const datesGroupedByIsoWeek = getDatesGroupedByIsoWeek(dates);
  const fullWeekRanges = Object.values(datesGroupedByIsoWeek)
    .filter(dates => dates.length === 7)
    .map(dates => ({
      beginDate: moment(dates[0]).format(format),
      endDate: moment(dates[6])
        .endOf('day')
        .format(format),
    }));
  return fullWeekRanges;
};

export {
  FORMAT_OPTIONS,
  isDate,
  getTimeZone,
  getTimeZones,
  formatTimeZone,
  getTimeZoneWithShortOffset,
  parseFromAnyDateToJSDate,
  parseFromDateToPlainDate,
  parseFromDateToPlainTime,
  parseFromDateToPlainDateTime,
  parseFromDateToPlainYearMonth,
  parseFromDateToInstantUTC,
  formatDate,
  formatDateTime,
  formatPlainDate,
  formatPlainDateTime,
  formatInstant,
  formatAnyDate,
  formatDateRange,
  formatDateTimeRange,
  formatAnyDateRange,
  formatDateFromNow,
  getTodayAsPlainDate,
  getNowAsPlainDateTime,
  getArrayOfWeekdays,
  getArrayOfDates,
  getArrayOfMonths,
  isDateBetweenDateRange,
  isDateRangeInDateRange,
  areDateRangesOverlapping,
  getIsoYearFromDate,
  getIsoWeekIdFromDate,
  getDateRangeFromIsoWeek,
  getStartOfIsoWeek,
  getEndOfIsoWeek,
  getStartOfIsoMonth,
  getEndOfIsoMonth,
  getStartOfIsoYear,
  getEndOfIsoYear,
  getDateRangeDiff,
  getDatesGroupedByIsoWeek,
  getFullWeekRangesFromDateRange,
};
